import { useMemo } from 'react';
import { useCustom } from '@refinedev/core';
import type { SortFieldOut } from '@/api/generated/schemas';

interface SortFieldOption {
  label: string;
  value: string;
}

/**
 * Loads the publication sort fields from
 * `GET /user_catalog/publications/sort_fields`. The field set is static, so it
 * is cached indefinitely for the session.
 *
 * `options` holds only the selectable fields — the ones an editor may pick.
 * `labels` covers every field including the scoped ones (series/ranking
 * position), which are never selectable but still need a name when rendering a
 * collection's built-in order.
 *
 * Note: a RELATIVE url is used (resolved against the axios baseURL). An absolute
 * url would be mangled by `dataProvider.custom`, which replaces all dots with
 * slashes (intended for dot-notation resources) — that breaks dotted hostnames.
 */
export const useSortFields = (): {
  options: SortFieldOption[];
  labels: Record<string, string>;
  isLoading: boolean;
} => {
  const { query } = useCustom<SortFieldOut[]>({
    url: `user_catalog/publications/sort_fields`,
    method: 'get',
    queryOptions: {
      queryKey: ['publication-sort-fields'],
      staleTime: Infinity,
    },
  });

  const raw = query.data?.data;

  const options = useMemo(
    () =>
      (Array.isArray(raw) ? raw : [])
        .filter((f) => f.selectable)
        .map((f) => ({ label: f.label, value: f.name })),
    [raw]
  );

  const labels = useMemo(
    () =>
      Object.fromEntries(
        (Array.isArray(raw) ? raw : []).map((f) => [f.name, f.label])
      ),
    [raw]
  );

  return { options, labels, isLoading: query.isLoading };
};
